<template>
  <div>
    <PageHeader title="Create SMS Campaign" showBack />
    <b-card>
      <b-form @submit.prevent="handleCreate">
        <b-form-group label="Campaign Name"><b-form-input v-model="form.name" required /></b-form-group>
        <b-form-group label="SMS Template (optional)">
          <b-form-select v-model="form.template" :options="templateOptions" @change="onTemplateChange" />
        </b-form-group>
        <b-form-group label="Message Body" :description="`${(form.body || '').length} characters`">
          <b-form-textarea v-model="form.body" rows="4" :required="!form.template" placeholder="Type your SMS message... or pick a template above" />
        </b-form-group>
        <b-form-group label="Select Recipients by Tags">
          <v-select v-model="form.tag_ids" :options="tags" label="name" :reduce="t => t.id" multiple placeholder="Select tags..." />
          <div v-if="form.tag_ids && form.tag_ids.length" class="mt-2 small">
            <span v-if="isCountLoading" class="text-muted">
              <b-spinner small /> Counting recipients...
            </span>
            <span v-else>
              <a href="#" @click.prevent="openRecipientsModal" class="font-weight-bold">
                {{ recipientCount }} {{ recipientCount === 1 ? 'recipient' : 'recipients' }}
              </a>
              <span class="text-muted ml-1">match the selected tags</span>
            </span>
          </div>
        </b-form-group>
        <div class="d-flex">
          <b-button type="submit" variant="primary" :disabled="isSaving" class="mr-2">{{ isSaving ? 'Creating...' : 'Create Campaign' }}</b-button>
          <b-button variant="outline-primary" :disabled="isSaving" @click="showScheduleModal = true">Schedule</b-button>
        </div>
      </b-form>
    </b-card>

    <b-modal v-model="showScheduleModal" title="Schedule SMS Campaign" @ok="handleScheduleCreate" ok-title="Create & Schedule" :ok-disabled="!scheduleDate || !scheduleTime || isSaving" centered>
      <p class="text-muted mb-3">Choose when this campaign should be sent:</p>
      <b-form-group label="Date">
        <b-form-datepicker v-model="scheduleDate" :min="minDate" />
      </b-form-group>
      <b-form-group label="Time">
        <b-form-timepicker v-model="scheduleTime" />
      </b-form-group>
    </b-modal>

    <b-modal v-model="showRecipientsModal" title="Recipients Preview" size="lg" hide-footer centered scrollable>
      <p class="text-muted mb-3">{{ recipientCount }} {{ recipientCount === 1 ? 'recipient matches' : 'recipients match' }} the selected tags.</p>
      <b-table :items="recipients" :fields="recipientFields" :busy="isRecipientsLoading" show-empty class="table-nowrap mb-0" responsive>
        <template #cell(full_name)="data">
          {{ data.item.first_name }} {{ data.item.last_name }}
        </template>
        <template #cell(tags)="data">
          <b-badge v-for="tag in data.item.tags" :key="tag.id" :style="{ backgroundColor: tag.color }" class="mr-1 text-white">
            {{ tag.name }}
          </b-badge>
        </template>
        <template #table-busy>
          <div class="text-center my-3"><b-spinner /></div>
        </template>
      </b-table>
      <div v-if="recipientCount > recipientsPerPage" class="mt-2 d-flex justify-content-end">
        <Pagination :totalRows="recipientCount" :perPage="recipientsPerPage" v-model="recipientsPage" @change="fetchRecipientsPage" />
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import PageHeader from '../../components/Ui/PageHeader.vue';
import Pagination from '../../components/Ui/Pagination.vue';

export default {
  name: 'CreateSMSCampaign',
  components: { PageHeader, Pagination },
  data() {
    return {
      form: { name: '', template: null, body: '', tag_ids: [], contact_ids: [] },
      templates: [],
      tags: [],
      isSaving: false,
      showScheduleModal: false,
      scheduleDate: '',
      scheduleTime: '',
      recipientCount: 0,
      isCountLoading: false,
      countDebounce: null,
      showRecipientsModal: false,
      recipients: [],
      recipientsPage: 1,
      recipientsPerPage: 15,
      isRecipientsLoading: false,
      recipientFields: [
        { key: 'full_name', label: 'Name' },
        { key: 'phone', label: 'Phone' },
        { key: 'email', label: 'Email' },
        { key: 'tags', label: 'Tags' },
      ],
    };
  },
  computed: {
    templateOptions() {
      return [{ value: null, text: 'No template — write a custom message' }, ...this.templates.map(t => ({ value: t.id, text: t.name }))];
    },
    minDate() {
      return new Date().toISOString().split('T')[0];
    },
  },
  watch: {
    'form.tag_ids': {
      handler() {
        if (this.countDebounce) clearTimeout(this.countDebounce);
        this.countDebounce = setTimeout(() => {
          this.fetchRecipientCount();
        }, 250);
      },
    },
  },
  methods: {
    ...mapActions('smsCampaign', ['createSMSCampaign', 'scheduleSMSCampaign']),
    ...mapActions('smsTemplate', ['getAllSMSTemplates']),
    ...mapActions('tag', ['getAllTags']),
    ...mapActions('contact', ['getAllContacts']),
    onTemplateChange(id) {
      const t = this.templates.find(tpl => tpl.id === id);
      if (t) this.form.body = t.body;
    },
    async fetchRecipientCount() {
      if (!this.form.tag_ids || !this.form.tag_ids.length) {
        this.recipientCount = 0;
        return;
      }
      this.isCountLoading = true;
      try {
        const res = await this.getAllContacts({
          tag_ids: this.form.tag_ids.join(','),
          limit: 1,
          offset: 0,
        });
        this.recipientCount = res?.data?.count ?? res?.count ?? 0;
      } catch (err) {
        this.recipientCount = 0;
      } finally {
        this.isCountLoading = false;
      }
    },
    async openRecipientsModal() {
      this.recipientsPage = 1;
      this.showRecipientsModal = true;
      await this.fetchRecipientsPage(1);
    },
    async fetchRecipientsPage(page) {
      this.recipientsPage = page;
      this.isRecipientsLoading = true;
      try {
        const res = await this.getAllContacts({
          tag_ids: this.form.tag_ids.join(','),
          limit: this.recipientsPerPage,
          offset: (page - 1) * this.recipientsPerPage,
        });
        this.recipients = res?.data?.results || res?.results || [];
        this.recipientCount = res?.data?.count ?? res?.count ?? 0;
      } catch (err) {
        this.recipients = [];
      } finally {
        this.isRecipientsLoading = false;
      }
    },
    async handleCreate() {
      this.isSaving = true;
      try {
        const user = this.$store.getters['auth/getLoggedInUser'];
        await this.createSMSCampaign({ ...this.form, school: user.school.id });
        this.makeToast('success', 'SMS campaign created');
        this.$router.push({ name: 'sms-campaigns-list' });
      } catch (err) { this.makeToast('danger', 'Failed to create SMS campaign'); }
      finally { this.isSaving = false; }
    },
    async handleScheduleCreate(evt) {
      evt.preventDefault();
      if (!this.scheduleDate || !this.scheduleTime) return;
      this.isSaving = true;
      try {
        const user = this.$store.getters['auth/getLoggedInUser'];
        const res = await this.createSMSCampaign({ ...this.form, school: user.school.id });
        const campaignId = res.data.data ? res.data.data.id : res.data.id;
        const scheduled_at = `${this.scheduleDate}T${this.scheduleTime}`;
        await this.scheduleSMSCampaign({ id: campaignId, data: { scheduled_at } });
        this.makeToast('success', 'SMS campaign scheduled');
        this.showScheduleModal = false;
        this.$router.push({ name: 'sms-campaigns-list' });
      } catch (err) { this.makeToast('danger', 'Failed to schedule campaign'); }
      finally { this.isSaving = false; }
    },
  },
  async created() {
    try {
      const [tplRes, tagRes] = await Promise.all([this.getAllSMSTemplates(), this.getAllTags()]);
      this.templates = tplRes.data.results || tplRes.data || [];
      this.tags = tagRes.data.results || tagRes.data || [];
    } catch (err) { /* ignore */ }
  },
};
</script>
